import React, { useEffect, useMemo, useState } from 'react';
import EntityTable from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { NoDataPlaceHolderTableRow } from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';
import axios from 'axios';
import { useUserId } from 'src/utils/Hooks/reduxSelectors';
import { buildLlmCrawlerPath, getAppName } from 'src/utils/app';
import { formatISODate } from 'src/utils/dateUtils';
import { EXPORT_DOWNLOAD_PAGE_SIZE } from 'src/providers/ExportContextProvider';

interface DownloadItem {
    downloadId: string;
    name: string;
    createdAt: string | number | Date | null;
    status: string;
    hasOutput: boolean;
    s3FilePath?: string;
}

const ShelfDownloadStatusDisplayName: Record<string, string> = {
    completed: 'Download',
    failed: 'Failed',
    processing: 'Processing',
};

const DownloadCell = ({ value, row, onDownload }: { value: string; row: DownloadItem; onDownload: (id: string) => void }) => {
    const [hovered, setHovered] = useState(false);
    const normalized = (value || '').toString().toLowerCase();
    const isCompletedStatus = ['completed'].includes(normalized);
    const isClickable = isCompletedStatus;
    const label = isCompletedStatus
        ? 'Download'
        : ShelfDownloadStatusDisplayName[normalized] || (value ? String(value) : 'Processing');

    return (
        <Flex style={{ alignItems: 'center' }}>
            <div
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                onClick={() => isClickable && onDownload(row.downloadId || row.downloadId)}
                onKeyDown={(e) => {
                    if (!isClickable) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onDownload(row.downloadId || row.downloadId);
                    }
                }}
                role={isClickable ? 'button' : 'cell'}
                tabIndex={isClickable ? 0 : -1}
                aria-label={isClickable ? 'Download' : undefined}
                style={{ cursor: isClickable ? 'pointer' : 'default', pointerEvents: 'auto' }}
            >
                <Text variant="body2" sx={{ textDecoration: isClickable && hovered ? 'underline' : 'none' }}>{label}</Text>
            </div>
        </Flex>
    );
};

const downloadColumnDefinition = (onDownload: (id: string) => void) => [
    {
        field: 'name',
        headerName: 'FILE NAME',
        tableColumnProps: {
            style: {
                width: '530px'
            }
        },
        valueFormatter: (value: string) => {
            const fileNameWithoutExtension = (value || '').replace('.csv', '');
            return (
                <Flex paddingLeft="8px">
                    <Text title={fileNameWithoutExtension} sx={{ width: '520px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} variant="body2">
                        {fileNameWithoutExtension}
                    </Text>
                </Flex>
            );
        }
    },
    {
        field: 'createdAt',
        headerName: 'DATE',
        tableColumnProps: {
            style: {
                width: '221px'
            }
        },
        valueFormatter: (value: string | number | Date | null) => {
            return (
                <Flex>
                    <div>
                        <Text variant="body2">{value ? formatISODate(String(value), 'M/D/YY') : '—'}</Text>
                    </div>
                </Flex>
            );
        }
    },
    {
        field: 'status',
        headerName: 'STATUS',
        valueFormatter: (value: string, row: DownloadItem) => {
            return <DownloadCell value={value} row={row} onDownload={onDownload} />;
        }
    }
];

const AtlasV2ShelfDownloadTable = () => {
    const [items, setItems] = useState<DownloadItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const userId = useUserId();

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                setLoading(true);
                setError(null);
                const path = buildLlmCrawlerPath(`downloads?userId=${encodeURIComponent(userId || '')}`);
                const { data } = await axios.get(path);
                const arr: any[] = Array.isArray(data?.downloads)
                    ? data.downloads
                    : [];
                const results: DownloadItem[] = arr.map((d) => {
                    const downloadId = String(d?.downloadId || '');
                    const name = String(d?.name || d?.fileName || d?.filename || d?.title || `Download ${downloadId}`);
                    const s3FilePath = d?.s3FilePath || undefined;
                    const createdAt = d?.createdTime || null;
                    let status = d.status

                    if (!status) status = s3FilePath ? 'Completed' : 'Processing';
                    const hasOutput = ['finished successfully', 'finished', 'success', 'completed', 'complete'].includes(status.toLowerCase());
                    return {  downloadId, name, createdAt, status, hasOutput, s3FilePath };
                }).filter((d) => d.downloadId);
                if (active) setItems(results.sort((a, b) => {
                    const at = a.createdAt ? new Date(a.createdAt as any).getTime() : -Infinity;
                    const bt = b.createdAt ? new Date(b.createdAt as any).getTime() : -Infinity;
                    return bt - at;
                }));
            } catch (e: any) {
                if (active) setError('Failed to load downloads');
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, [userId]);

    const onDownload = (downloadId: string) => {
        if (!downloadId) return;
        (async () => {
            try {
                const item = items.find((it) => it.downloadId === downloadId);
                const apiPath = buildLlmCrawlerPath('download');
                const { data } = await axios.get(apiPath, {
                    params: { downloadId, userId }
                });
                const presignedUrl: string | undefined = data?.url || data?.URL || data?.uri || data?.presignedUrl;
                if (!presignedUrl) return;

                const anchor = document.createElement('a');
                anchor.style.display = 'none';
                anchor.href = presignedUrl;
                const fallbackFromUrl = (() => {
                    try {
                        const u = new URL(presignedUrl);
                        const pathname = decodeURIComponent(u.pathname);
                        const last = pathname.split('/').pop() || `download-${downloadId}`;
                        return last.includes('.') ? last : `${last}.csv`;
                    } catch {
                        return `download-${downloadId}.csv`;
                    }
                })();
                anchor.download = item?.name || fallbackFromUrl;
                document.body.appendChild(anchor);
                anchor.click();
                setTimeout(() => {
                    try { anchor.remove(); } catch { }
                }, 100);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('Failed to download from presigned URL', e);
            }
        })();
    };

    const COL_DEFS = useMemo(() => downloadColumnDefinition(onDownload), []);

    return (
        <EntityTable
            isLoading={loading}
            rowData={items || []}
            columnDefs={COL_DEFS}
            shouldModifyColumnDefs={false}
            initialPageSize={EXPORT_DOWNLOAD_PAGE_SIZE}
            showPagination={false}
            skeletonRows={EXPORT_DOWNLOAD_PAGE_SIZE}
            appTableProps={{
                noDataPlaceholder: (
                    <NoDataPlaceHolderTableRow
                        title="No downloads yet"
                        description={`After downloading files from your ${getAppName()} account, visit this page to access them.`}
                    />
                )
            }}
        />
    );
};

export default AtlasV2ShelfDownloadTable;


